import { supabase } from "../supabase";
import { cachingService, getCachingTime } from "./cache";
import { secureLog, secureError } from "./emailPrivacy";
import * as Sentry from "@sentry/react";

const CONFIG = {
  baseApiUrl: import.meta.env.VITE_BASE_URL,
  SECRET: import.meta.env.SECRET,
  TOKEN: import.meta.env.TOKEN,
};

const baseApiUrl = CONFIG.baseApiUrl;

// Utility function to generate an AES key from a given secret
async function generateKey(secret) {
  const enc = new TextEncoder();
  const keyMaterial = await crypto.subtle.digest("SHA-256", enc.encode(secret));
  return crypto.subtle.importKey(
    "raw",
    keyMaterial,
    { name: "AES-GCM" },
    false,
    ["encrypt", "decrypt"]
  );
}

// Encrypt function using AES-GCM
async function encryptData(data, secret) {
  const key = await generateKey(secret);
  const iv = crypto.getRandomValues(new Uint8Array(12)); // Initialization vector
  const encodedData = new TextEncoder().encode(data);
  const encryptedData = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv },
    key,
    encodedData
  );

  return {
    iv: Array.from(iv),
    encrypted: Array.from(new Uint8Array(encryptedData)),
  };
}

// Encrypt token with native Web Crypto API
let cryptotext;
encryptData(CONFIG.TOKEN, CONFIG.SECRET).then((result) => {
  cryptotext = result;
});

// Get Auth Token
const getAuthToken = async () => {
  try {
    const {
      data: { session },
      error,
    } = await supabase.auth.getSession();

    if (error) {
      secureError("Error getting session:", error);
      return { "x-access-token": JSON.stringify(cryptotext) };
    }

    if (session?.access_token) {
      secureLog("Using access token from session");
      return { Authorization: `Bearer ${session.access_token}` };
    }

    secureLog("No session found, using fallback token");
    return { "x-access-token": JSON.stringify(cryptotext) };
  } catch (error) {
    secureError("Error in getAuthToken:", error);
    return { "x-access-token": JSON.stringify(cryptotext) };
  }
};

// Fetch API Wrapper
export const fetchApi = async (url) => {
  const fullUrl = `${baseApiUrl}${url}`;
  
  // Add breadcrumb for API call
  Sentry.addBreadcrumb({
    category: 'api',
    message: `API Call: ${url}`,
    level: 'info',
    data: {
      url: fullUrl,
      method: 'GET',
    },
  });

  const headers = await getAuthToken();
  const opt = {
    headers: {
      ...headers,
    },
  };

  try {
    const response = await fetch(fullUrl, opt);
    const text = await response.text();

    if (response.status === 403) {
      const error = new Error("Forbidden");
      error.status = 403;
      throw error;
    }
    if (response.status === 500) {
      const error = new Error("fetchApi error");
      try {
        error.response = JSON.parse(text);
      } catch {
        error.response = { raw: text?.slice(0, 200) };
      }
      secureError("Bad server fetch request:", error);

      Sentry.captureException(error, {
        tags: { api: true, endpoint: url, method: "GET" },
        extra: { url: fullUrl, status: response.status, response: error.response },
      });
      throw error;
    }

    if (!response.ok) {
      const error = new Error(`Request failed (${response.status})`);
      error.status = response.status;
      if (text?.trim().startsWith("<")) {
        error.message = `Server returned HTML instead of JSON. Check API URL (${url}) and ensure the endpoint exists.`;
      }
      throw error;
    }

    if (!text?.trim()) {
      return {};
    }
    if (text?.trim().startsWith("<")) {
      const error = new Error(
        `Server returned HTML instead of JSON. Check VITE_BASE_URL and API endpoint (${url}).`
      );
      error.status = response.status;
      throw error;
    }
    try {
      return JSON.parse(text);
    } catch (parseError) {
      const error = new Error(
        `Invalid JSON from API. Check endpoint: ${url}`
      );
      error.cause = parseError;
      throw error;
    }
  } catch (error) {
    // Capture network errors and other exceptions
    if (error.name !== 'Error' || !error.response) {
      Sentry.captureException(error, {
        tags: {
          api: true,
          endpoint: url,
          method: 'GET',
          errorType: 'network',
        },
        extra: {
          url: fullUrl,
        },
      });
    }
    throw error;
  }
};

// Fetch POST API
export const fetchPostApi = async (url, bodyContent, method = "POST") => {
  const fullUrl = `${baseApiUrl}${url}`;
  
  // Add breadcrumb for API call
  Sentry.addBreadcrumb({
    category: 'api',
    message: `API Call: ${url}`,
    level: 'info',
    data: {
      url: fullUrl,
      method,
    },
  });

  const headers = await getAuthToken();
  const opt = {
    method,
    body: JSON.stringify(bodyContent),
    headers: {
      "Content-Type": "application/json",
      ...headers,
    },
  };

  try {
    const response = await fetch(fullUrl, opt);
    if (!response.ok) {
      const error = new Error("fetchPostApi error");
      error.status = response.status;
      const contentType = response.headers.get("content-type") || "";
      if (contentType.includes("application/json")) {
        error.response = await response.json();
      } else {
        error.response = {
          success: false,
          message: `Server error (${response.status}). Please try again.`,
        };
      }
      secureError("Bad server fetch request:", error);
      
      // Capture error in Sentry
      Sentry.captureException(error, {
        tags: {
          api: true,
          endpoint: url,
          method,
        },
        extra: {
          url: fullUrl,
          status: response.status,
          response: error.response,
          body: bodyContent,
        },
      });
      
      throw error;
    }
    return response.json();
  } catch (error) {
    // Capture network errors and other exceptions
    if (error.name !== 'Error' || !error.response) {
      Sentry.captureException(error, {
        tags: {
          api: true,
          endpoint: url,
          method,
          errorType: 'network',
        },
        extra: {
          url: fullUrl,
          body: bodyContent,
        },
      });
    }
    throw error;
  }
};

export const fetchPostFormData = async (url, bodyContent, method = "POST") => {
  const fullUrl = `${baseApiUrl}${url}`;
  
  // Add breadcrumb for API call
  Sentry.addBreadcrumb({
    category: 'api',
    message: `API Call: ${url}`,
    level: 'info',
    data: {
      url: fullUrl,
      method,
      contentType: 'multipart/form-data',
    },
  });

  const headers = await getAuthToken();

  const opt = {
    method,
    body: bodyContent,
    headers: {
      ...headers,
    },
  };

  try {
    const response = await fetch(fullUrl, opt);
    if (!response.ok) {
      const error = new Error("fetchPostFormData error");
      error.response = await response.json();
      secureError("Bad server fetch request:", error);
      
      // Capture error in Sentry
      Sentry.captureException(error, {
        tags: {
          api: true,
          endpoint: url,
          method,
          contentType: 'form-data',
        },
        extra: {
          url: fullUrl,
          status: response.status,
          response: error.response,
        },
      });
      
      throw error;
    }
    return response.json();
  } catch (error) {
    // Capture network errors and other exceptions
    if (error.name !== 'Error' || !error.response) {
      Sentry.captureException(error, {
        tags: {
          api: true,
          endpoint: url,
          method,
          errorType: 'network',
          contentType: 'form-data',
        },
        extra: {
          url: fullUrl,
        },
      });
    }
    throw error;
  }
};

// Fetch Action
export const fetchAction =
  (url, types, ownApi = true) =>
  async (dispatch) => {
    const [REQUEST, SUCCESS, FAILURE] = types;
    let opt = {};
    const headers = await getAuthToken();
    if (ownApi) {
      url = `${baseApiUrl}${url}`;
      opt = {
        headers: {
          ...headers,
        },
      };
    }

    const cachedData = cachingService.get(url);
    if (cachedData) {
      dispatch({ type: SUCCESS, payload: cachedData?.data || cachedData });
      return { success: true, response: cachedData };
    }

    dispatch({ type: REQUEST, payload: url });

    try {
      // Add breadcrumb for API call
      Sentry.addBreadcrumb({
        category: 'api',
        message: `API Action: ${url}`,
        level: 'info',
        data: {
          url,
          method: 'GET',
          actionType: REQUEST,
        },
      });

      const response = await fetch(url, opt);
      if (!response.ok) {
        throw new Error("Bad response fetchAction");
      }
      const PAYLOAD = await response.json();
      if (!PAYLOAD.success) {
        throw new Error("Bad response fetchAction");
      }
      // const data = await response.json();
      cachingService.set(url, PAYLOAD, getCachingTime(url), REQUEST); // Cache for 1 hour
      dispatch({ type: SUCCESS, payload: PAYLOAD.data });
      return { success: true, response: PAYLOAD };
    } catch (error) {
      dispatch({ type: FAILURE, payload: error });
      
      // Capture error in Sentry
      Sentry.captureException(error, {
        tags: {
          api: true,
          endpoint: url,
          method: 'GET',
          actionType: REQUEST,
        },
        extra: {
          url,
          actionTypes: { REQUEST, SUCCESS, FAILURE },
        },
      });
      
      return { success: false, response: error };
    }
  };

// Fetch Action POST
export const fetchActionPost =
  (url, bodyContent, types, ownApi = true) =>
  async (dispatch) => {
    const [REQUEST, SUCCESS, FAILURE] = types;
    let opt = {};
    const headers = await getAuthToken();
    if (ownApi) {
      url = `${baseApiUrl}${url}`;
      opt = {
        method: "POST",
        body: JSON.stringify(bodyContent),
        headers: {
          "Content-Type": "application/json",
          ...headers,
        },
      };
    }

    dispatch({ type: REQUEST, payload: bodyContent });

    try {
      // Add breadcrumb for API call
      Sentry.addBreadcrumb({
        category: 'api',
        message: `API Action POST: ${url}`,
        level: 'info',
        data: {
          url,
          method: 'POST',
          actionType: REQUEST,
        },
      });

      const response = await fetch(url, opt);
      if (!response.ok) {
        throw new Error("Bad response fetchActionPost");
      }
      const PAYLOAD = await response.json();
      if (!PAYLOAD.success) {
        throw new Error("Bad response fetchActionPost");
      }
      dispatch({ type: SUCCESS, payload: PAYLOAD.data });
      return { success: true, response: PAYLOAD };
    } catch (error) {
      dispatch({ type: FAILURE, payload: error });

      // Capture error in Sentry
      Sentry.captureException(error, {
        tags: {
          api: true,
          endpoint: url,
          method: 'POST',
          actionType: REQUEST,
        },
        extra: {
          url,
          body: bodyContent,
          actionTypes: { REQUEST, SUCCESS, FAILURE },
        },
      });

      return { success: false, response: error };
    }
  };
